import { getContentHtmlStringValueFromRawRichTextContentState, IRichContentEditorState } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { SAVED_REPLY_VARIABLES } from '../../../models/Support/savedReplySubstitution';
import { ISupportSavedReply } from '../../../models/Support/ISupportSavedReply';
import { createContentStateWithHtmlStringValue } from '../../../models/utils/richContentUtils';
import {
	useCreateSavedReplyMutation,
	useDeleteSavedReplyMutation,
	useSavedRepliesQuery,
	useUpdateSavedReplyMutation,
} from '../../../queries/Support/useSupportSavedReplies';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import {
	IRichContentDocumentEditorConfig,
	RichContentDocumentEditor,
} from '../../../web/components/richContent/RichContentDocumentEditor';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { PlusIcon } from './SettingsIcons';
import { savedRepliesTabStyles as styles } from './styles/savedRepliesTabStyles';
import { settingsStyles as st } from './styles/settingsStyles';

interface ISavedRepliesTabProps {
	mailboxId: string;
}

const EditorConfig: IRichContentDocumentEditorConfig = {
	autoresizeToFitContent: true,
	minHeight: 120,
	skipDefaultEmailStyleFont: true,
	toolbar:
		'undo redo | fontselect fontsizeselect | bold italic underline | forecolor | link | listmenu | alignmentmenu',
};

/** Extract HTML string from editor state */
const getEditorHtml = (state: IRichContentEditorState | null): string => {
	if (!state) return '';
	const raw = state.getRawRichTextContent();
	return getContentHtmlStringValueFromRawRichTextContentState(raw) || '';
};

/**
 * Strip HTML tags for preview display.
 * Uses DOMParser, which builds an inert document — unlike `div.innerHTML`, it never
 * fetches resources or fires parser-driven handlers (`<img onerror>`, `<svg onload>`),
 * so untrusted reply bodies can't execute anything while we extract their text.
 */
const stripHtml = (html: string): string => {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || '';
};

export function SavedRepliesTab({ mailboxId }: ISavedRepliesTabProps) {
	const { data, isLoading, isError } = useSavedRepliesQuery(mailboxId);
	const [mutationError, setMutationError] = React.useState<string | null>(null);
	const createMutation = useCreateSavedReplyMutation(mailboxId, {
		onSuccess: () => setMutationError(null),
		onError: error => setMutationError(getApiErrorMessage(error, 'Failed to create saved reply.')),
	});
	const updateMutation = useUpdateSavedReplyMutation(mailboxId, {
		onSuccess: () => setMutationError(null),
		onError: error => setMutationError(getApiErrorMessage(error, 'Failed to save saved reply.')),
	});
	const deleteMutation = useDeleteSavedReplyMutation(mailboxId, {
		onSuccess: () => setMutationError(null),
		onError: error => setMutationError(getApiErrorMessage(error, 'Failed to delete saved reply.')),
	});

	const [searchTerm, setSearchTerm] = React.useState('');
	const [expandedId, setExpandedId] = React.useState<string | null>(null);
	const [isCreating, setIsCreating] = React.useState(false);

	const [editName, setEditName] = React.useState('');
	const [editEditorState, setEditEditorState] = React.useState<IRichContentEditorState>(
		createContentStateWithHtmlStringValue('')
	);

	const [createName, setCreateName] = React.useState('');
	const [createEditorState, setCreateEditorState] = React.useState<IRichContentEditorState>(
		createContentStateWithHtmlStringValue('')
	);
	const [pendingDeleteId, setPendingDeleteId] = React.useState<string | null>(null);

	const replies = data?.values ?? [];
	const filteredReplies = replies.filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()));

	const handleExpand = (reply: ISupportSavedReply) => {
		if (expandedId === reply.id) {
			setExpandedId(null);
			return;
		}
		setExpandedId(reply.id);
		setEditName(reply.name);
		setEditEditorState(createContentStateWithHtmlStringValue(reply.body || ''));
	};

	const handleUpdate = () => {
		if (!expandedId || !editName.trim()) return;
		setMutationError(null);
		const body = getEditorHtml(editEditorState);
		updateMutation.mutate(
			{ id: expandedId, name: editName.trim(), body },
			{
				onSuccess: () => {
					setExpandedId(null);
				},
			}
		);
	};

	const handleCreate = () => {
		if (!createName.trim()) return;
		setMutationError(null);
		const body = getEditorHtml(createEditorState);
		createMutation.mutate(
			{ name: createName.trim(), body },
			{
				onSuccess: () => {
					setIsCreating(false);
					setCreateName('');
					setCreateEditorState(createContentStateWithHtmlStringValue(''));
				},
			}
		);
	};

	const handleDelete = (id: string) => {
		setPendingDeleteId(id);
	};

	const confirmDelete = () => {
		if (!pendingDeleteId) return;
		setMutationError(null);
		deleteMutation.mutate(pendingDeleteId, {
			onSuccess: () => {
				if (expandedId === pendingDeleteId) setExpandedId(null);
				setPendingDeleteId(null);
			},
		});
	};

	const handleInsertCreateToken = (token: string) =>
		setCreateEditorState(createContentStateWithHtmlStringValue(getEditorHtml(createEditorState) + token));

	const handleInsertEditToken = (token: string) =>
		setEditEditorState(createContentStateWithHtmlStringValue(getEditorHtml(editEditorState) + token));

	const handleCancelCreate = () => {
		setIsCreating(false);
		setCreateName('');
		setCreateEditorState(createContentStateWithHtmlStringValue(''));
	};

	if (isLoading) return <LoadingSpinner />;
	if (isError) return <div className={css(st.error)}>Failed to load saved replies.</div>;

	return (
		<div>
			<div className={css(styles.header)}>
				<div>
					<h2 className={css(st.sectionTitle)}>Saved Replies</h2>
					<div className={css(st.sectionDescription, styles.headerDescription)}>
						Canned responses your team can drop into any reply.
					</div>
				</div>
				<Button onClick={() => setIsCreating(true)} disabled={isCreating} styles={[st.newButton]}>
					<PlusIcon />
					New saved reply
				</Button>
			</div>

			<div className={css(styles.searchBar)}>
				<Input
					type='text'
					placeholder='Search saved replies…'
					value={searchTerm}
					onChange={e => setSearchTerm(e.target.value)}
					styles={[st.input]}
				/>
			</div>

			{mutationError && <div className={css(st.error)}>{mutationError}</div>}

			{isCreating && (
				<div className={css(st.formCard)}>
					<h3 className={css(st.formTitle)}>New Saved Reply</h3>
					<SavedReplyForm
						editorState={createEditorState}
						isSaving={createMutation.isPending}
						name={createName}
						namePlaceholder='Enter reply name...'
						onCancel={handleCancelCreate}
						onEditorStateChange={setCreateEditorState}
						onInsertToken={handleInsertCreateToken}
						onNameChange={setCreateName}
						onSave={handleCreate}
					/>
				</div>
			)}

			<div className={css(st.list)}>
				{filteredReplies.length === 0 && (
					<div className={css(st.emptyState)}>
						{searchTerm ? 'No saved replies match your search.' : 'No saved replies yet. Create one to get started.'}
					</div>
				)}
				{filteredReplies.map(reply => (
					<div key={reply.id} className={css(styles.replyItem)}>
						<div className={css(styles.replyHeader)} onClick={() => handleExpand(reply)}>
							<div className={css(styles.replyInfo)}>
								<span className={css(styles.replyName)}>{reply.name}</span>
								<span className={css(styles.replyPreview)}>
									{stripHtml(reply.body).substring(0, 100)}
									{stripHtml(reply.body).length > 100 ? '...' : ''}
								</span>
							</div>
							<div className={css(styles.replyActions)}>
								<Button
									variant='ghost'
									size='sm'
									onClick={e => {
										e.stopPropagation();
										handleDelete(reply.id);
									}}
									title='Delete'
								>
									&times;
								</Button>
							</div>
						</div>

						{expandedId === reply.id && (
							<div className={css(st.editForm)}>
								<SavedReplyForm
									editorState={editEditorState}
									isSaving={updateMutation.isPending}
									name={editName}
									onCancel={() => setExpandedId(null)}
									onEditorStateChange={setEditEditorState}
									onInsertToken={handleInsertEditToken}
									onNameChange={setEditName}
									onSave={handleUpdate}
								/>
							</div>
						)}
					</div>
				))}
			</div>
			{pendingDeleteId && (
				<ConfirmModal
					title='Delete Saved Reply'
					message='Are you sure you want to delete this saved reply? This action cannot be undone.'
					confirmLabel='Delete'
					variant='danger'
					onConfirm={confirmDelete}
					onCancel={() => setPendingDeleteId(null)}
				/>
			)}
		</div>
	);
}

/** Shared create/edit form: name, body editor, "Insert variable" select, and Save/Cancel. */
function SavedReplyForm({
	editorState,
	isSaving,
	name,
	namePlaceholder,
	onCancel,
	onEditorStateChange,
	onInsertToken,
	onNameChange,
	onSave,
}: {
	editorState: IRichContentEditorState;
	isSaving: boolean;
	name: string;
	namePlaceholder?: string;
	onCancel: () => void;
	onEditorStateChange: (state: IRichContentEditorState) => void;
	onInsertToken: (token: string) => void;
	onNameChange: (name: string) => void;
	onSave: () => void;
}) {
	return (
		<>
			<div className={css(st.field)}>
				<label className={css(st.fieldLabel)}>Name</label>
				<Input
					type='text'
					value={name}
					onChange={e => onNameChange(e.target.value)}
					styles={[st.input]}
					placeholder={namePlaceholder}
				/>
			</div>
			<div className={css(st.field)}>
				<div className={css(styles.editorHeader)}>
					<label className={css(st.fieldLabel)}>Body</label>
					<Select
						value=''
						onValueChange={value => {
							if (value) onInsertToken(value);
						}}
					>
						<SelectTrigger styles={st.selectPill}>
							<SelectValue placeholder='Insert variable...' />
						</SelectTrigger>
						<SelectContent>
							{SAVED_REPLY_VARIABLES.map(variable => (
								<SelectItem key={variable.token} value={variable.token}>
									{variable.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={css(st.editorContainer)}>
					<RichContentDocumentEditor
						contentState={editorState}
						onContentStateChanged={onEditorStateChange}
						config={EditorConfig}
					/>
				</div>
			</div>
			<div className={css(st.formActionsTight)}>
				<Button onClick={onSave} disabled={isSaving || !name.trim()}>
					{isSaving ? 'Saving...' : 'Save'}
				</Button>
				<Button onClick={onCancel}>Cancel</Button>
			</div>
		</>
	);
}
